import React, { useState, useRef, useEffect } from 'react';
import { Folder, X } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { userErrorMessage } from '../../utils/userError';
import Modal from '../ui/Modal';

const CreateFolderModal = ({
    onClose,
    onCreate,
    initialName = '',
    title,
    submitLabel
}) => {
    const { t } = useLanguage();
    // Defaults are resolved here (not as prop defaults) so they can go through
    // t() — a caller MAY still override with an explicit title/submitLabel
    // (e.g. the Rename flow builds its own dynamic title).
    const resolvedTitle = title ?? t('fmCreateFolderTitle', 'Create New Folder');
    const resolvedSubmitLabel = submitLabel ?? t('fmCreate', 'Create');
    const [name, setName] = useState(initialName);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef(null);

    // Focus and select input on mount
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedName = name.trim();

        if (!trimmedName) {
            setError(t('fmNameRequired', 'Name is required'));
            return;
        }

        if (trimmedName.length > 255) {
            setError(t('fmNameTooLong', 'Name is too long (max 255 characters)'));
            return;
        }

        // Check for invalid characters
        if (/[<>:"/\\|?*]/.test(trimmedName)) {
            setError(t('fmNameInvalidChars', 'Name contains invalid characters'));
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            await onCreate(trimmedName);
            onClose();
        } catch (err) {
            setError(userErrorMessage(err, t));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        // No backdrop dismiss (a stray click must not drop a typed name); Escape
        // closes only this dialog, never the File Manager behind it.
        <Modal onClose={onClose} closeOnBackdrop={false} cardTestId="fm-create-folder-dialog">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-pai-hairline">
                    <div className="flex items-center gap-3">
                        <Folder className="text-amber-400" size={22} />
                        <h3 className="text-[19px] font-bold text-white">
                            {resolvedTitle}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="pai-icon-btn w-[38px] h-[38px]"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="mb-4">
                        <label
                            htmlFor="folder-name"
                            className="block text-[13px] font-semibold text-pai-secondary mb-2"
                        >
                            {t('commonName', 'Name')}
                        </label>
                        <input
                            ref={inputRef}
                            type="text"
                            id="folder-name"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                setError('');
                            }}
                            placeholder={t('fmEnterFolderName', 'Enter folder name')}
                            className={`
                                pai-input w-full h-[44px] px-4 text-[14px]
                                ${error ? 'border-pai-red' : ''}
                            `}
                        />
                        {error && (
                            <p className="mt-2 text-[13px] text-pai-red">
                                {error}
                            </p>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="pai-btn-ghost"
                        >
                            {t('commonCancel', 'Cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading || !name.trim()}
                            className="pai-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {isLoading && (
                                <div className="animate-spin h-4 w-4 border-2 border-pai-void border-t-transparent rounded-full" />
                            )}
                            {resolvedSubmitLabel}
                        </button>
                    </div>
                </form>
            </Modal>
    );
};

export default CreateFolderModal;
